import { useCallback, useEffect, useRef, useState } from 'react';

import { partnerAuthService } from 'features/partner-auth/services/partnerAuth.service';

import { partnerPortalService } from '../services/partnerPortal.service';
import type { DashboardAccess } from '../types';

export type RevealState =
  | 'locked'
  | 'sending'
  | 'awaitingCode'
  | 'verifying'
  | 'revealed'
  | 'error';

interface SendOtpResult {
  challengeId: string;
  otpExpiresIn: number;
  sentTo: string;
  devOtp?: string;
}

interface UseDashboardRevealReturn {
  state: RevealState;
  errorMessage: string | null;
  access: DashboardAccess | null;
  challenge: SendOtpResult | null;
  revealSecondsLeft: number;
  sendOtp: () => Promise<void>;
  resendOtp: () => Promise<void>;
  verify: (code: string) => Promise<void>;
  lockNow: () => void;
  cancel: () => void;
}

export const useDashboardReveal = (): UseDashboardRevealReturn => {
  const [state, setState] = useState<RevealState>('locked');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<SendOtpResult | null>(null);
  const [access, setAccess] = useState<DashboardAccess | null>(null);
  const [revealSecondsLeft, setRevealSecondsLeft] = useState(0);

  const revealTokenRef = useRef<string | null>(null);
  const countdownTimerRef = useRef<number | null>(null);

  const clearCountdown = useCallback(() => {
    if (countdownTimerRef.current !== null) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const lockNow = useCallback(() => {
    clearCountdown();
    revealTokenRef.current = null;
    setAccess(null);
    setChallenge(null);
    setRevealSecondsLeft(0);
    setErrorMessage(null);
    setState('locked');
  }, [clearCountdown]);

  const cancel = useCallback(() => {
    lockNow();
  }, [lockNow]);

  useEffect(() => () => clearCountdown(), [clearCountdown]);

  const startRevealCountdown = useCallback(
    (totalSeconds: number) => {
      clearCountdown();
      setRevealSecondsLeft(totalSeconds);
      countdownTimerRef.current = window.setInterval(() => {
        setRevealSecondsLeft((prev) => {
          if (prev <= 1) {
            clearCountdown();
            revealTokenRef.current = null;
            setAccess(null);
            setState('locked');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clearCountdown],
  );

  const sendOtp = useCallback(async () => {
    setErrorMessage(null);
    setState('sending');
    try {
      const result = await partnerAuthService.sendDashboardOtp();
      setChallenge(result);
      setState('awaitingCode');
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.message ?? err?.message ?? 'send_failed',
      );
      setState('error');
    }
  }, []);

  const resendOtp = useCallback(async () => {
    if (!challenge) {
      await sendOtp();
      return;
    }
    setErrorMessage(null);
    try {
      const result = await partnerAuthService.sendDashboardOtp();
      setChallenge(result);
      setState('awaitingCode');
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.message ?? err?.message ?? 'send_failed',
      );
      setState('error');
    }
  }, [challenge, sendOtp]);

  const verify = useCallback(
    async (code: string) => {
      if (!challenge) return;
      setErrorMessage(null);
      setState('verifying');
      try {
        const { revealToken, expiresIn } =
          await partnerAuthService.verifyDashboardOtp(
            challenge.challengeId,
            code,
          );
        revealTokenRef.current = revealToken;
        const data = await partnerPortalService.getDashboardAccess(revealToken);
        setAccess(data);
        setState('revealed');
        startRevealCountdown(expiresIn);
      } catch (err: any) {
        setErrorMessage(
          err?.response?.data?.message ?? err?.message ?? 'verify_failed',
        );
        setState('awaitingCode');
      }
    },
    [challenge, startRevealCountdown],
  );

  return {
    state,
    errorMessage,
    access,
    challenge,
    revealSecondsLeft,
    sendOtp,
    resendOtp,
    verify,
    lockNow,
    cancel,
  };
};
